import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/lib/api/auth";
import { useAuthStore } from "@/stores/auth-store";
import { useTranslations } from "@/i18n/useTranslations";
import { Button } from "@/components/ui/button";
import { Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ImpersonateBannerProps {
  onExitComplete?: () => void;
}

export function ImpersonateBanner({ onExitComplete }: ImpersonateBannerProps) {
  const t = useTranslations("auth");
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const setTokensWithRemember = useAuthStore((s) => s.setTokensWithRemember);
  const setUser = useAuthStore((s) => s.setUser);
  const [isExiting, setIsExiting] = useState(false);

  const handleExit = async () => {
    try {
      setIsExiting(true);
      const data = await authApi.exitImpersonate(refreshToken!);

      setTokensWithRemember(data.accessToken, data.refreshToken, false);
      setUser(data.user);
      sessionStorage.removeItem("impersonator");
      sessionStorage.removeItem("impersonated_tokens");
      onExitComplete?.();
      toast.success(t("impersonate.exit"));

      navigate("/dashboard", { replace: true });
    } catch {
      toast.error(t("impersonate.exitError"));
    } finally {
      setIsExiting(false);
    }
  };

  const displayName = user?.displayName || user?.email || user?.phoneNumber || "";

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-10 bg-gradient-to-r from-primary to-primary-dark text-primary-foreground flex items-center justify-center gap-3 text-sm shadow-card">
      <Shield className="h-4 w-4" />
      <span>{t("impersonate.banner", { name: displayName })}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleExit}
        disabled={isExiting}
        className="h-7 px-3 text-xs text-primary-foreground hover:bg-overlay/20"
      >
        {isExiting ? (
          <>
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            {t("impersonate.exiting")}
          </>
        ) : (
          t("impersonate.exit")
        )}
      </Button>
    </div>
  );
}
